import {Box, Stack, ToggleButton, ToggleButtonGroup, Typography} from "@mui/material";
import {Floorplan, RoomImage} from "../../../api/apiClient";
import {BrowserMinimap} from "./BrowserMinimap";
import {BrowserRoomMenu} from "./BrowserRoomMenu";
import {NavLayout, needsTabSwitcher, PANEL_GUTTER} from "./useNavLayout";
import "../../../animations/fadeIn.css";

export type NavTab = "floorplan" | "rooms";

const TAB_LABELS: Record<NavTab, string> = {
    floorplan: "Floorplan",
    rooms: "Rooms"
};

/**
 * Any radius past half the element's height rounds its ends into semicircles, so one value works for the
 * track and the buttons inside it without either having to know the other's height.
 */
const PILL_RADIUS_PX = 999;

export function availableNavTabs(floorPlans: Floorplan[], rooms: RoomImage[]): NavTab[] {
    const tabs: NavTab[] = [];
    if (floorPlans.length > 0) tabs.push("floorplan");
    if (rooms.length > 0) tabs.push("rooms");
    return tabs;
}

export interface NavigationSwitcherProps {
    tabs: NavTab[];
    activeTab: NavTab;
    setActiveTab: (tab: NavTab) => void;
}

/** The Floorplan/Rooms pill. Rendered separately from its sections, as it lives in the sheet header. */
export function NavigationSwitcher(props: NavigationSwitcherProps) {
    const {tabs, activeTab, setActiveTab} = props;

    return <ToggleButtonGroup
        exclusive
        fullWidth
        value={activeTab}
        size={"small"}
        onChange={(_, value: NavTab | null) => {
            // ToggleButtonGroup reports null when the active button is pressed again, but there is always
            // one section showing.
            if (value) setActiveTab(value);
        }}
        sx={{
            width: "100%",
            // Explicit, as the app has no CssBaseline.
            boxSizing: "border-box",
            backgroundColor: "action.hover",
            borderRadius: PILL_RADIUS_PX,
            // No track padding, so the selected pill's edge lands on the panel gutter.
            p: 0,
            "& .MuiToggleButton-root": {
                border: 0,
                borderRadius: PILL_RADIUS_PX,
                boxSizing: "border-box",
                px: 2,
                py: 0.5,
                textTransform: "none",
                fontWeight: 500,
                "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    "&:hover": {backgroundColor: "primary.dark"}
                }
            }
        }}
    >
        {tabs.map(tab => <ToggleButton key={tab} value={tab}>{TAB_LABELS[tab]}</ToggleButton>)}
    </ToggleButtonGroup>;
}

export interface NavigationSectionsProps {
    floorPlans: Floorplan[];
    rooms: RoomImage[];
    currentPanorama: string;
    changePanorama: (panorama: string) => void;
    layout: NavLayout;
    visibleTabs: NavTab[];
    showViewpoints: boolean;
    setShowViewpoints: (showViewpoints: boolean) => void;
}

/**
 * The navigation content itself, identical in every shell. Both sections visible means stacked under
 * headings; with the switcher in use only the active one is rendered and the pill acts as its heading.
 */
export function NavigationSections(props: NavigationSectionsProps) {
    const {floorPlans, rooms, currentPanorama, changePanorama, layout, visibleTabs,
        showViewpoints, setShowViewpoints} = props;

    const showHeadings = !needsTabSwitcher(layout);
    const bothVisible = visibleTabs.length > 1;

    return <Stack sx={{flexGrow: 1, minHeight: 0, overflow: "hidden"}}>
        {visibleTabs.map(tab => <Stack
            key={tab}
            className={"fade-in"}
            sx={{
                // Stacked, the plan takes the height its proportions need and the room list scrolls in
                // what is left. A single section takes everything.
                flex: bothVisible && tab === "floorplan" ? "0 0 auto" : "1 1 auto",
                minHeight: 0
            }}
        >
            {showHeadings && <Typography variant={"subtitle1"} fontWeight={600} sx={{px: PANEL_GUTTER, pt: 2, pb: 1}}>
                {TAB_LABELS[tab]}
            </Typography>}

            {tab === "floorplan"
                ? <BrowserMinimap floorPlans={floorPlans} currentPanorama={currentPanorama}
                                  changePanorama={changePanorama} showViewpoints={showViewpoints}
                                  setShowViewpoints={setShowViewpoints} capPlanHeight={bothVisible}/>
                : <BrowserRoomMenu rooms={rooms} currentPanorama={currentPanorama} changePanorama={changePanorama}/>}
        </Stack>)}
    </Stack>;
}

/** Shown instead of the panel contents when a tour has neither floorplans nor room images. */
export function EmptyNavigation() {
    return <Box sx={{p: 2}}>
        <Typography variant={"body2"} color={"text.secondary"}>No navigation available for this tour.</Typography>
    </Box>;
}
